import { Injectable, EventEmitter } from '@angular/core';
import { UserNotification } from '@models/userNotification.model';
import { LoggingService } from './logging.service';
import { HttpBackend, HttpClient } from '@angular/common/http';

import { saveAs } from 'file-saver-es';
import { BrowserTabVisibilityChangeService } from './browser-tab-visibility-change.service';
import { GraphService } from '@services/graph.service';
import { NotificationType } from '../mystudies/_study_constants';

@Injectable({
  providedIn: 'root',
})
export class UserNotificationService {
  public notificationsChanged: EventEmitter<UserNotification[]> = new EventEmitter();
  public unreadCountChanged: EventEmitter<number> = new EventEmitter();
  private NOTIFICATIONS_STORAGE_KEY = 'ey-notifications';
  private notifications: UserNotification[];
  public environment: string;

  constructor(
    private logger: LoggingService,
    private http: HttpClient,
    handler: HttpBackend,
    private browserTabVisibilityChangeService: BrowserTabVisibilityChangeService,
    private graphService: GraphService,
  ) {
    this.http = new HttpClient(handler);
    this.logger.info(this.constructor.name, 'ctor', `Start`);
  }

  public initialize() {
    this.getNotifications();
    this.environment = this.graphService.getEnvironment();
  }

  /**
   * Adds notification message; does not try to find/update existing notification
   *
   * @param message – the message you want to relay to the user
   * @param isErrorMessage – if this is a message for an error pass true and the text will be red in the dialog otherwise pass false
   * @param url – (Optional) if you want the message to have a clickable destination, for example when a long running operation is complete and you want them to easily navigate to the result
   *
   * @returns void
   */
  public addNotification(message: string, isErrorMessage: boolean, url?: string) {
    this.logger.info(this.constructor.name, 'addNotification', `Start`);
    const notification = new UserNotification();
    notification.key = Date.now().toString(); // make it somewhat unique
    this.notifications.push(notification);

    notification.isRead = false;
    notification.isErrorMessage = isErrorMessage;
    notification.message = message;
    notification.updatedDate = new Date();
    notification.url = url;

    this.updateAndNotify(true);

    if (url && this.browserTabVisibilityChangeService.isBrowserTabRecentlyActive()) this.downloadUrl(url);
  }

  /**
   * Adds or updates a notification message based on the provided key
   *
   * @param key – unique identifier that tells the service to add or update a given message.
   * @param message – the message you want to relay to the user
   * @param isErrorMessage – if this is a message for an error pass true and the text will be red in the dialog otherwise pass false
   * @param url – (Optional) if you want the message to have a clickable destination, for example when a long running operation is complete and you want them to easily navigate to the result
   *
   * @returns void
   */
  public addUpdateNotification(key: string, message: string, isErrorMessage: boolean, url?: string) {
    this.logger.info(this.constructor.name, 'addUpdateNotification', `Start`);
    let notification = this.getNotification(key);
    if (!notification) {
      notification = new UserNotification();
      notification.key = key;
      this.notifications.push(notification);
    }

    notification.isRead = false;
    notification.isErrorMessage = isErrorMessage;
    notification.message = message;
    notification.updatedDate = new Date();
    notification.url = url;

    this.updateAndNotify(true);
  }

  /**
   * Mark notification message as unread based on the provided key
   *
   * @param key – unique identifier that tells the service the message to update
   *
   * @returns void
   *
   */
  public markNotificationUnread(key: string) {
    this.logger.info(this.constructor.name, 'markNotificationUnread', `Start`);
    const notification = this.getNotification(key);
    if (notification) {
      notification.isRead = false;

      this.updateAndNotify(true);
    }
  }

  /**
   * Mark notification message as read based on the provided key
   *
   * @param key – unique identifier that tells the service the message to update
   *
   * @returns void
   *
   */
  public markNotificationRead(key: string) {
    this.logger.info(this.constructor.name, 'markNotificationRead', `Start`);
    const notification = this.getNotification(key);
    if (notification) {
      notification.isRead = true;

      this.updateAndNotify(true);
    }
  }

  /**
   * Mark all notification messages as read
   *
   * @returns void
   *
   */
  public markAllNotificationsRead() {
    this.logger.info(this.constructor.name, 'markAllNotificationsRead', `Start`);
    for (const notification of this.getNotifications()) {
      notification.isRead = true;
    }
    this.updateAndNotify(true);
  }

  /**
   * Delete all notification messages
   *
   * @returns void
   *
   */
  public removeAllNotifications() {
    this.logger.info(this.constructor.name, 'removeAllNotifications', `Start`);
    this.notifications = [];

    this.updateAndNotify(true);
  }

  /**
   * Delete notification message as read based on the provided key
   *
   * @param key – unique identifier that tells the service the message to delete
   *
   * @returns void
   *
   */
  public removeNotification(key: string) {
    if (this.getNotification(key)) {
      this.logger.info(this.constructor.name, 'removeNotification', `Start`);
      this.notifications = this.notifications.filter((x) => x.key !== key);

      this.updateAndNotify(true);
    }
  }

  /**
   * Get count of all unread notification messages
   *
   * @returns number indicating count of unread messages
   *
   */
  public getUnreadNotificationCount(): number {
    this.logger.info(this.constructor.name, 'getUnreadNotificationCount', `Start`);
    return this.getNotifications().filter((x) => x.isRead !== true).length;
  }

  /**
   * Get a single notification message based on the provided key
   *
   * @param key – unique identifier that tells the service the UserNotification to return
   *
   * @returns  single UserNotification if it exists
   *
   */
  public getNotification(key: string): UserNotification {
    this.logger.info(this.constructor.name, 'getNotification', `Start: key=[${key}]`);
    return this.getNotifications().find((x) => x.key === key);
  }

  /**
   * Get All notification messages
   *
   * @returns Array of UserNotification objects
   *
   */
  public getNotifications(): UserNotification[] {
    this.logger.info(this.constructor.name, 'getNotifications', `Start`);

    if (this.notifications) {
      return this.notifications;
    } else {
      const notificationsString = localStorage.getItem(this.NOTIFICATIONS_STORAGE_KEY);
      if (notificationsString) {
        this.notifications = JSON.parse(notificationsString);
      } else {
        this.notifications = [];
      }
      this.updateAndNotify(true);
    }

    return this.notifications;
  }

  private updateAndNotify(unreadChanged: boolean) {
    localStorage.setItem(this.NOTIFICATIONS_STORAGE_KEY, JSON.stringify(this.notifications));

    this.notificationsChanged.emit(this.notifications);

    if (unreadChanged) {
      this.unreadCountChanged.emit(this.getUnreadNotificationCount());
    }
  }

  private downloadUrl(url) {
    this.http.get(url, { observe: 'response', responseType: 'blob' as 'json' }).subscribe((response: any) => {
      let filename = url.split('/').pop().split('#')[0].split('?')[0];
      const contentDispositionHeader = response.headers.get('Content-Disposition');

      if (contentDispositionHeader) {
        filename = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      }

      const blob = new Blob([response.body], { type: response.body.type.toString() });
      saveAs(blob, filename);
    });
  }
  /**
   * Adds Report notification message; does not try to find/update existing notification
   *
   * @param message – the message you want to relay to the user
   * @param isErrorMessage – if this is a message for an error pass true and the text will be red in the dialog otherwise pass false
   * @param url – (Optional) if you want the message to have a clickable destination, for example when a long running operation is complete and you want them to easily navigate to the result
   *
   * @returns void
   */
  public addReportNotification(key: string, message: string, isErrorMessage: boolean, url?: string, isRequestCompleted?: boolean) {
    if (this.environment === 'DEV' || this.environment === 'QA') {
      this.logger.info(this.constructor.name, 'addReportNotification', `Start`);
      let notification = this.getNotification(key);
      if (!notification) {
        notification = new UserNotification();
        notification.key = key;
        this.notifications.push(notification);
        notification.notificationType = NotificationType.Report;
        notification.createdDate = new Date();
      }
      if (notification && isRequestCompleted) {
        notification.key = `${key}_${Date.now().toString()}`;
      }

      notification.isRead = false;
      notification.isErrorMessage = isErrorMessage;
      notification.message = message;
      notification.updatedDate = new Date();
      notification.url = url;
      notification.isRequestCompleted = isRequestCompleted;
      notification.notificationType = NotificationType.Report;
      if (!!notification.updatedDate) {
        notification.duration = notification.updatedDate.getTime() - notification.createdDate.getTime();
      }

      this.updateAndNotify(true);

      if (url && this.browserTabVisibilityChangeService.isBrowserTabRecentlyActive()) this.downloadUrl(url);
    }
  }
}
